// src/api/client.js - Flipkart-style Authentication Client
import axios from 'axios';
import { getToken, clearAuthSession } from '../auth/authStorage';

// Base URL for the API
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3100/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    // Add Bearer token if available
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling 401 and other errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Handle 401 unauthorized - auto logout
    if (error.response?.status === 401) {
      clearAuthSession();
      // Removed forced redirect to allow guest browsing when non-critical APIs fail
    }

    return Promise.reject(error);
  }
);

// Utility function for handling API responses consistently
export const handleApiResponse = async (apiCall) => {
  try {
    const response = await apiCall;
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'API request failed',
      status: error.response?.status,
      data: error.response?.data,
    };
  }
};

export default api;